import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { api, ApiError } from "./api";

export interface Session {
  user: {
    id: string;
    displayName: string;
    email: string;
    locale: "pt-BR" | "en" | "es";
  };
  tenant: {
    id: string;
    name: string;
    plan: string;
  };
  roles: string[];
  platformRoles: string[];
  featureFlags: Record<string, boolean>;
}

interface AuthContextValue {
  session: Session | null;
  loading: boolean;
  login(email: string, password: string): Promise<void>;
  registerAccount(displayName: string, email: string, password: string): Promise<void>;
  logout(): Promise<void>;
  refresh(): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const response = await api<{ data: Session }>("/session");
      setSession(response.data);
    } catch (error) {
      if (!(error instanceof ApiError) || error.status !== 401) {
        console.error(error);
      }
      setSession(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      loading,
      async login(email, password) {
        await api("/auth/login", { method: "POST", body: { email, password } });
        await refresh();
      },
      async registerAccount(displayName, email, password) {
        await api("/auth/register", { method: "POST", body: { displayName, email, password } });
        await refresh();
      },
      async logout() {
        await api("/session/logout", { method: "POST" });
        setSession(null);
      },
      refresh,
    }),
    [loading, refresh, session],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth precisa estar dentro de AuthProvider");
  return context;
}
